import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  PhoneOff,
} from 'lucide-react';
import CallModal from '@/components/CallModal';
import { supabase } from '@/lib/supabase';
import {
  createRingingCallSession,
  endCallSession,
  markRingingCallMissed,
} from '@/governor/calls/CallsLifecycleAuthority';
import { authorizeCallsRuntimeAction } from '@/governor/calls/CallsRuntimeControlAdapter';
import { observeCallsRuntimeHandler } from '@/governor/calls/CallsRuntimeObservationBridge';
import { emitCallsRuntimeEvent } from '@/lib/governorEventBus';
import {
  callsIncomingChannel,
} from '@/governor/calls/CallsRealtimeChannels';

const startKinsCircleRingback = () => {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return () => {
      stopRingback();};
    const ctx = new AudioCtx();
    let stopped = false;
    let timer: any = null;

    const beep = () => {
      if (stopped) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 440;
      gain.gain.value = 0.06;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      setTimeout(() => {
        try { osc.stop(); } catch {}
      }, 650);
    };

    beep();
    timer = setInterval(beep, 2200);

    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
      try { ctx.close(); } catch {}
    };
  } catch {
    return () => {};
  }
};

const makeCallRoomName = (a: string, b: string) =>
  ['kinscircle-call', a.trim().toLowerCase(), b.trim().toLowerCase()]
    .sort()
    .join('__')
    .replace(/[^a-z0-9_@.-]/g, '_');

export default function NativeCallPage() {
  const navigate = useNavigate();
  const sentRef = useRef(false);
  const activeCallSessionIdRef = useRef<string | null>(null);
  const deliveryTimeoutRef = useRef<number | null>(null);
  const stopRingbackRef =
    useRef<(() => void) | null>(null);

  const [
    callerIdentity,
    setCallerIdentity,
  ] = useState<{
    email: string;
    name: string;
  } | null>(null);

  const [
    activeRoomName,
    setActiveRoomName,
  ] = useState<string | null>(null);

  const [
    mediaOpen,
    setMediaOpen,
  ] = useState(false);

  const [
    startFailure,
    setStartFailure,
  ] = useState<string | null>(null);

  const params =
    new URLSearchParams(
      window.location.search,
    );

  const mode = params.get('mode') === 'voice' ? 'voice' : 'video';
  const receiverEmail = params.get('to') || '';
  const receiverName = params.get('name') || 'Family member';

  const endCall = async () => {
    observeCallsRuntimeHandler('NativeCallPage.endCall');

    if (deliveryTimeoutRef.current !== null) {
      window.clearTimeout(deliveryTimeoutRef.current);
      deliveryTimeoutRef.current = null;
    }

    try {
      stopRingbackRef.current?.();
    } catch {}
    stopRingbackRef.current = null;

    const callSessionId = activeCallSessionIdRef.current;
    const endedAt = new Date().toISOString();

    if (callSessionId) {
      const endResult =
        await endCallSession({
          callSessionId,
        });

      if (!endResult.ok) {
        console.warn(
          '[KinsCircle Call] native end synchronization failed:',
          endResult.error,
        );
      }
    }

    emitCallsRuntimeEvent({
      source: 'NativeCallPage',
      stage: 'NATIVE_CALL_ENDED_BY_CALLER',
      status: 'success',
      callSessionId,
      receiverEmail,
      mode,
      timestamp: endedAt,
    });

    navigate('/app', {
      replace: true,
      state: {
        returnFromCall: true,
        openMessages: true,
        callSessionId,
      },
    });

    window.setTimeout(() => {
      try {
        window.dispatchEvent(
          new CustomEvent('kc:call-returned-to-messages', {
            detail: {
              callSessionId,
              receiverEmail,
              mode,
              returnedAt: endedAt,
            },
          }),
        );
      } catch {}
    }, 0);
  };

  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;

    const stopRingback = startKinsCircleRingback();
    stopRingbackRef.current = stopRingback;

    const start = async () => {
    observeCallsRuntimeHandler('NativeCallPage.startCall');

    const runtimeControl =
      authorizeCallsRuntimeAction('start', {
        source: 'native-call-page',
      });

    if (!runtimeControl.allowed) {
      console.warn(
        'Calls Governor denied runtime start.',
        runtimeControl,
      );
      return;
    }

            emitCallsRuntimeEvent({
        source: 'NativeCallPage',
        stage: 'NATIVE_CALL_START_AUTHORIZED',
        status: 'success',
        receiverEmail,
        mode,
      });

      const { data: userData } = await supabase.auth.getUser();
      const callerEmail = userData?.user?.email || '';
      if (!callerEmail || !receiverEmail) {
        const reason =
          !callerEmail ? 'missing caller email' : 'missing receiver email';
        const observedAt = new Date().toISOString();

        emitCallsRuntimeEvent({
          source: 'NativeCallPage',
          stage: 'NATIVE_CALL_IDENTITY_MISSING',
          status: 'failure',
          receiverEmail,
          mode,
          reason,
          timestamp: observedAt,
        });

        try {
          stopRingbackRef.current?.();
        } catch {}
        stopRingbackRef.current = null;

        alert(`Call failed: ${reason}.`);

        if (!callerEmail) {
          navigate('/login?next=%2Fapp', {
            replace: true,
            state: {
              returnFromCall: true,
              callFailure: reason,
            },
          });
        } else {
          navigate('/app', {
            replace: true,
            state: {
              returnFromCall: true,
              callFailure: reason,
            },
          });
        }
        return;
      }

      const callerName =
        userData?.user?.user_metadata?.full_name ||
        userData?.user?.user_metadata?.name ||
        callerEmail;

      const roomName = makeCallRoomName(callerEmail, receiverEmail);

      const callSessionResult =
        await createRingingCallSession({
          callerEmail,
          receiverEmail,
          callerName,
          receiverName,
          roomName,
          mode,
        });

      const callSessionId =
        callSessionResult.callSessionId;
      activeCallSessionIdRef.current =
        callSessionId;

      if (
        !callSessionResult.ok ||
        !callSessionId
      ) {
        const reason =
          callSessionResult.error ||
          'The call session could not be created.';

        setStartFailure(reason);

        try {
          stopRingbackRef.current?.();
        } catch {}

        stopRingbackRef.current = null;

        emitCallsRuntimeEvent({
          source: 'NativeCallPage',
          stage:
            'CALL_SESSION_CREATE_FAILED',
          status: 'failure',
          callerEmail,
          receiverEmail,
          roomName,
          mode,
          reason,
        });

        return;
      }

      setCallerIdentity({
        email: callerEmail,
        name: callerName,
      });

      setActiveRoomName(
        roomName,
      );

      setMediaOpen(true);

      emitCallsRuntimeEvent({
        source: 'NativeCallPage',
        stage: callSessionId
          ? 'CALL_SESSION_CREATED'
          : 'CALL_SESSION_CREATE_FAILED',
        status: callSessionId ? 'success' : 'failure',
        callSessionId,
        callerEmail,
        receiverEmail,
        roomName,
        mode,
        reason: callSessionId
          ? undefined
          : 'call_sessions insert returned no session id',
      });


            emitCallsRuntimeEvent({
        source: 'NativeCallPage',
        stage: 'DIRECT_INCOMING_CALL_DELIVERY_ATTEMPTED',
        status: 'attempt',
        callSessionId,
        callerEmail,
        receiverEmail,
        roomName,
        mode,
      });

      let receiverAcknowledged = false;

      const incomingChannel =
        supabase
          .channel(
            callsIncomingChannel(receiverEmail),
            {
              config: {
                broadcast: {
                  self: false,
                },
              },
            },
          )
          .on(
            'broadcast',
            {
              event:
                'incoming-call-ack',
            },
            payload => {
              const data =
                payload?.payload ??
                payload;

              const ackSessionId =
                data?.callSessionId ??
                data?.call_session_id ??
                null;

              if (
                ackSessionId !==
                callSessionId
              ) {
                return;
              }

              receiverAcknowledged = true;

              emitCallsRuntimeEvent({
                source:
                  'NativeCallPage',
                stage:
                  'INCOMING_CALL_ACKNOWLEDGED',
                status:
                  'success',
                callSessionId,
                callerEmail,
                receiverEmail,
                roomName,
                mode,
                acknowledgedAt:
                  data?.acknowledgedAt ??
                  new Date().toISOString(),
                deliveryPath:
                  data?.deliveryPath ??
                  'unknown',
              });
            },
          );

      try {
        await new Promise<void>((resolve, reject) => {
          let settled = false;

          const timeout = window.setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(
              new Error(
                'Incoming-call channel subscription timed out.',
              ),
            );
          }, 8000);

          incomingChannel.subscribe(async status => {
            if (status === 'SUBSCRIBED') {
              if (settled) return;

              try {
                await incomingChannel.send({
                  type: 'broadcast',
                  event: 'incoming-call',
                  payload: {
                    fromEmail: callerEmail,
                    fromName: callerName,
                    callerEmail,
                    receiverEmail,
                    mode,
                    roomName,
                    callSessionId,
                    call_session_id: callSessionId,
                  },
                });

                settled = true;
                window.clearTimeout(timeout);

                emitCallsRuntimeEvent({
                  source: 'NativeCallPage',
                  stage: 'DIRECT_INCOMING_CALL_DELIVERED',
                  status: 'success',
                  callSessionId,
                  callerEmail,
                  receiverEmail,
                  roomName,
                  mode,
                });

                resolve();
              } catch (error) {
                settled = true;
                window.clearTimeout(timeout);
                reject(error);
              }
            }

            if (
              status === 'CHANNEL_ERROR' ||
              status === 'TIMED_OUT' ||
              status === 'CLOSED'
            ) {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeout);
              reject(
                new Error(
                  `Incoming-call channel failed: ${status}`,
                ),
              );
            }
          });
        });
      } catch (error) {
        emitCallsRuntimeEvent({
          source: 'NativeCallPage',
          stage: 'DIRECT_INCOMING_CALL_DELIVERY_FAILED',
          status: 'failure',
          callSessionId,
          callerEmail,
          receiverEmail,
          roomName,
          mode,
          error:
            error instanceof Error
              ? error.message
              : String(error),
        });
      } finally {
        await supabase.removeChannel(incomingChannel);
      }

      emitCallsRuntimeEvent({
        source: 'NativeCallPage',
        stage: 'CALL_RELAY_DELIVERY_ATTEMPTED',
        status: 'attempt',
        callSessionId,
        callerEmail,
        receiverEmail,
        roomName,
        mode,
      });

      await fetch('/.netlify/functions/send-call-push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: receiverEmail,
          fromName: callerName,
          callerEmail,
          receiverEmail,
          mode,
          roomName,
          callSessionId,
          call_session_id: callSessionId,
        }),
      })
        .then((response) => {
          emitCallsRuntimeEvent({
            source: 'NativeCallPage',
            stage: response.ok
              ? 'CALL_RELAY_DELIVERY_ACCEPTED'
              : 'CALL_RELAY_DELIVERY_REJECTED',
            status: response.ok ? 'success' : 'failure',
            callSessionId,
            callerEmail,
            receiverEmail,
            roomName,
            mode,
            httpStatus: response.status,
          });
        })
        .catch((error) => {
          emitCallsRuntimeEvent({
            source: 'NativeCallPage',
            stage: 'CALL_RELAY_DELIVERY_FAILED',
            status: 'failure',
            callSessionId,
            callerEmail,
            receiverEmail,
            roomName,
            mode,
            error: error instanceof Error
              ? error.message
              : String(error),
          });
        });

      deliveryTimeoutRef.current = window.setTimeout(async () => {
        if (!callSessionId) return;

        const endedAt = new Date().toISOString();

        if (!receiverAcknowledged) {
          emitCallsRuntimeEvent({
            source:
              'NativeCallPage',
            stage:
              'INCOMING_CALL_ACK_TIMEOUT',
            status:
              'timeout',
            callSessionId,
            callerEmail,
            receiverEmail,
            roomName,
            mode,
            reason:
              'Receiver runtime did not acknowledge the incoming call.',
            timestamp:
              endedAt,
          });
        }

        try {
          const missedResult =
            await markRingingCallMissed({
              callSessionId,
              callerEmail,
              receiverEmail,
              roomName,
            });

          if (!missedResult.ok) {
            emitCallsRuntimeEvent({
              source: 'NativeCallPage',
              stage: 'INCOMING_CALL_TIMEOUT_SYNC_FAILED',
              status: 'failure',
              callSessionId,
              callerEmail,
              receiverEmail,
              roomName,
              mode,
              error: missedResult.error || 'Unknown timeout synchronization failure',
              timestamp: endedAt,
            });

            try {
              stopRingbackRef.current?.();
            } catch {}
            stopRingbackRef.current = null;

            navigate('/app', {
              replace: true,
              state: {
                returnFromCall: true,
                openMessages: true,
                callSessionId,
                callFailure: 'timeout-sync-failed',
              },
            });

            window.setTimeout(() => {
              try {
                window.dispatchEvent(
                  new CustomEvent(
                    'kc:call-returned-to-messages',
                    {
                      detail: {
                        callSessionId,
                        receiverEmail,
                        mode,
                        returnedAt: endedAt,
                        status: 'timeout-sync-failed',
                      },
                    },
                  ),
                );
              } catch {}
            }, 0);

            return;
          }

          /*
           * Timeout evidence is authoritative only when this exact
           * call was still ringing and was successfully changed to
           * missed. Accepted, connected, or ended calls produce no
           * stale timeout evidence.
           */
          if (!missedResult.changed) return;

          emitCallsRuntimeEvent({
            source: 'NativeCallPage',
            stage: 'INCOMING_CALL_DELIVERY_TIMEOUT',
            status: 'timeout',
            callSessionId,
            callerEmail,
            receiverEmail,
            roomName,
            mode,
            reason:
              'This call remained ringing for 30 seconds without a verified answer.',
            timestamp: endedAt,
          });

          try {
            stopRingbackRef.current?.();
          } catch {}
          stopRingbackRef.current = null;

          navigate('/app', {
            replace: true,
            state: {
              returnFromCall: true,
              openMessages: true,
              callSessionId,
              callStatus: 'missed',
            },
          });

          window.setTimeout(() => {
            try {
              window.dispatchEvent(
                new CustomEvent(
                  'kc:call-returned-to-messages',
                  {
                    detail: {
                      callSessionId,
                      receiverEmail,
                      mode,
                      returnedAt: endedAt,
                      status: 'missed',
                    },
                  },
                ),
              );
            } catch {}
          }, 0);
        } finally {
          deliveryTimeoutRef.current = null;
        }
      }, 30000);
    };

    start();

    return () => {
      if (deliveryTimeoutRef.current !== null) {
        window.clearTimeout(deliveryTimeoutRef.current);
        deliveryTimeoutRef.current = null;
      }

      try {
        stopRingbackRef.current?.();
      } catch {}
      stopRingbackRef.current = null;
    };
  }, [mode, navigate, receiverEmail, receiverName]);


  useEffect(() => {
    if (
      !mediaOpen ||
      !activeCallSessionIdRef.current
    ) {
      return;
    }

    emitCallsRuntimeEvent({
      source: 'NativeCallPage',
      stage: 'CALLER_END_CONTROL_RENDERED',
      status: 'success',
      callSessionId:
        activeCallSessionIdRef.current,
      receiverEmail,
      mode,
    });
  }, [
    mediaOpen,
    mode,
    receiverEmail,
  ]);

  const stopCallerRingback = () => {
    try {
      stopRingbackRef.current?.();
    } catch {}

    stopRingbackRef.current = null;
  };

  if (startFailure) {
    return (
      <div className="fixed inset-0 z-[99999] bg-slate-950 text-white flex flex-col items-center justify-center px-6 text-center">
        <PhoneOff className="h-14 w-14 text-red-400" />

        <h1 className="mt-5 text-2xl font-bold">
          Call could not start
        </h1>

        <p className="mt-3 max-w-md text-sm text-white/70">
          {startFailure}
        </p>

        <button
          type="button"
          onClick={() => void endCall()}
          className="mt-6 rounded-full bg-white px-6 py-3 font-bold text-slate-950"
        >
          Return to KinsCircle
        </button>
      </div>
    );
  }

  if (
    !callerIdentity ||
    !activeRoomName ||
    !activeCallSessionIdRef.current
  ) {
    return (
      <div className="fixed inset-0 z-[99999] bg-slate-950 text-white flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin" />

        <div className="text-center">
          <h1 className="text-xl font-bold">
            Calling {receiverName}
          </h1>

          <p className="mt-1 text-sm text-white/65">
            Preparing the secure call…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[99999] bg-black">
      <button
        type="button"
        aria-label="End call"
        onClick={() => {
          emitCallsRuntimeEvent({
            source: 'NativeCallPage',
            stage: 'CALLER_END_CONTROL_PRESSED',
            status: 'attempt',
            callSessionId:
              activeCallSessionIdRef.current,
            receiverEmail,
            mode,
          });

          void endCall();
        }}
        className="fixed bottom-6 left-1/2 z-[100001] flex h-16 w-16 -translate-x-1/2 items-center justify-center rounded-full bg-red-600 text-white shadow-2xl active:bg-red-700"
      >
        <PhoneOff className="h-7 w-7" />
      </button>

      <CallModal
        open={mediaOpen}
        onOpenChange={(open) => {
          setMediaOpen(open);

          if (!open) {
            void endCall();
          }
        }}
        mode={mode}
        selfEmail={callerIdentity.email}
        selfName={callerIdentity.name}
        peerEmail={receiverEmail}
        peerName={receiverName}
        initiator={true}
        roomName={activeRoomName}
        callSessionId={
          activeCallSessionIdRef.current
        }
        onConnected={
          stopCallerRingback
        }
      />
    </div>
  );

}
